import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

// icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

// project import
import apiClient from 'utils/apiClient';

const generateSecurePassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%&*';
  const all = upper + lower + digits + symbols;

  let pwd = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ];

  for (let i = 4; i < 12; i++) {
    pwd.push(all[Math.floor(Math.random() * all.length)]);
  }

  return pwd.sort(() => 0.5 - Math.random()).join('');
};

export default function StaffUserDialog({
  open,
  onClose,
  onSuccess,
  roles = [],
  preselectedRole = ''
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    isActive: true
  });

  const [showPassword, setShowPassword] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      const initialPassword = generateSecurePassword();
      setFormData({
        name: '',
        email: '',
        password: initialPassword,
        role: preselectedRole || (roles[0]?.key || 'admin'),
        phone: '',
        isActive: true
      });
      setShowPassword(true);
      setErrorMsg('');
      setCopied(false);
    }
  }, [open, preselectedRole, roles]);

  const handleGenerateNewPassword = () => {
    const newPwd = generateSecurePassword();
    setFormData((prev) => ({ ...prev, password: newPwd }));
    setCopied(false);
  };

  const handleCopyCredentials = () => {
    const roleObj = roles.find((r) => r.key === formData.role || r._id === formData.role);
    const roleName = roleObj?.name || formData.role;
    const loginUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login';

    const text = [
      `🔐 Admin Staff Login Credentials`,
      `---------------------------------`,
      `Login URL: ${loginUrl}`,
      `Email: ${formData.email || '[email]'}`,
      `Password: ${formData.password}`,
      `Role: ${roleName}`,
      `---------------------------------`
    ].join('\n');

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setErrorMsg('Staff name is required');
      return;
    }
    if (!formData.email.trim()) {
      setErrorMsg('Staff email is required');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role || 'admin',
        phone: formData.phone.trim(),
        isActive: formData.isActive
      };

      const res = await apiClient.post('/api/admin/staff', payload);
      if (onSuccess) {
        onSuccess(res.data?.staff || payload);
      }
      onClose();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to create staff user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Generate Role User Account
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Create an admin login with email, generated password, and assigned role permissions.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Full Name */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Full Name"
                placeholder="e.g. John Miller"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            {/* Email Address */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Login Email"
                type="email"
                placeholder="e.g. john@shirishsparkle.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>

            {/* Assigned Role */}
            <Grid size={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="staff-role-select-label">Assign Role</InputLabel>
                <Select
                  labelId="staff-role-select-label"
                  label="Assign Role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.key || r._id} value={r.key}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({r.key})
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Password with generator */}
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel htmlFor="generated-password-input">Generated Login Password</InputLabel>
                <OutlinedInput
                  id="generated-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  label="Generated Login Password"
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ mr: 0.5 }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoFixHighIcon />}
                        onClick={handleGenerateNewPassword}
                        sx={{ fontSize: '0.75rem', py: 0.2 }}
                      >
                        New
                      </Button>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Grid>

            {/* Phone */}
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Contact Phone (Optional)"
                placeholder="e.g. +91 9876543210"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>

            {/* Active Status & Copy Credentials */}
            <Grid size={12}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Account Active (Can log in immediately)</Typography>}
                />

                <Button
                  size="small"
                  variant={copied ? 'contained' : 'outlined'}
                  color={copied ? 'success' : 'inherit'}
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopyCredentials}
                  sx={{ textTransform: 'none' }}
                >
                  {copied ? 'Copied!' : 'Copy Credentials'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={submitting}>
            {submitting ? 'Generating Account...' : 'Create Login Account'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
